/* Custom SVG visualizers for the 5 character metrics - no generic progress bars */
import React from "react";

export const METRIC_META = {
  mood: { label: "Nálada", color: "#FDE047", desc: "Jak se tvoje postava cítí" },
  aura: { label: "Aura", color: "#22D3EE", desc: "Pozitivní dojem online" },
  trust: { label: "Důvěra", color: "#1E3A8A", desc: "Síla vztahů a podpory" },
  safety: { label: "Bezpečí", color: "#10B981", desc: "Ochrana před rizikem" },
  stress: { label: "Stres", color: "#F43F5E", desc: "Vnitřní napětí (méně = líp)" },
};

function clamp(v) {
  return Math.max(0, Math.min(100, v));
}

function MoodBlob({ value }) {
  const v = clamp(value);
  const speed = 6 - (v / 100) * 3;
  return (
    <div className="flex items-center justify-center w-full h-full">
      <div
        className="w-16 h-16 bg-[#FDE047] border-2 border-black animate-blob-morph"
        style={{ animationDuration: `${speed}s`, borderRadius: "60% 40% 50% 50%" }}
      />
    </div>
  );
}

function AuraCircle({ value }) {
  const v = clamp(value);
  const scale = 0.5 + v / 200;
  return (
    <div className="flex items-center justify-center w-full h-full relative">
      <div
        className="absolute w-20 h-20 rounded-full bg-[#22D3EE] blur-2xl animate-pulse-aura"
        style={{ opacity: 0.35 + v / 180, transform: `scale(${scale})` }}
      />
      <div className="w-10 h-10 rounded-full bg-black z-10 border-2 border-white" />
    </div>
  );
}

function TrustConstellation({ value }) {
  const v = clamp(value);
  const dots = 8;
  const active = Math.round((v / 100) * dots);
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {Array.from({ length: dots }).map((_, i) => {
        const angle = (i / dots) * Math.PI * 2;
        const cx = 50 + Math.cos(angle) * 30;
        const cy = 50 + Math.sin(angle) * 30;
        const on = i < active;
        return (
          <g key={i}>
            {i > 0 && on && i - 1 < active && (
              <line
                x1={50 + Math.cos(((i - 1) / dots) * Math.PI * 2) * 30}
                y1={50 + Math.sin(((i - 1) / dots) * Math.PI * 2) * 30}
                x2={cx}
                y2={cy}
                stroke="#1E3A8A"
                strokeWidth="2"
              />
            )}
            <circle
              cx={cx}
              cy={cy}
              r={on ? 5 : 3}
              fill={on ? "#1E3A8A" : "#fff"}
              stroke="#0a0a0a"
              strokeWidth="2"
            />
          </g>
        );
      })}
    </svg>
  );
}

function SafetyRings({ value }) {
  const v = clamp(value);
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {[0, 1, 2].map((i) => {
        const r = 15 + i * 12;
        const thickness = v > i * 33 ? 4 : 1.5;
        const color = v > i * 33 ? "#10B981" : "#0a0a0a33";
        return (
          <circle
            key={i}
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={thickness}
            strokeDasharray={v < i * 33 ? "4 4" : ""}
          />
        );
      })}
      <circle cx="50" cy="50" r="5" fill="#10B981" stroke="#0a0a0a" strokeWidth="2" />
    </svg>
  );
}

function StressLine({ value }) {
  const v = clamp(value);
  const amp = 5 + (v / 100) * 15;
  const freq = 2 + (v / 100) * 4;
  const pts = [];
  for (let x = 0; x <= 100; x += 5) {
    const y = 50 + Math.sin((x / 100) * Math.PI * freq) * amp;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke="#F43F5E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={v > 50 ? "animate-heartbeat" : ""}
      />
    </svg>
  );
}

const VIZ = {
  mood: MoodBlob,
  aura: AuraCircle,
  trust: TrustConstellation,
  safety: SafetyRings,
  stress: StressLine,
};

export function MetricTile({ metric, value, delta = 0, size = 120 }) {
  const meta = METRIC_META[metric];
  const Viz = VIZ[metric];
  return (
    <div
      className="brut-card p-3 flex flex-col gap-2 relative overflow-hidden"
      data-testid={`metric-${metric}`}
    >
      <div className="flex items-center justify-between text-xs font-black uppercase tracking-wider">
        <span>{meta.label}</span>
        <span className="tabular-nums">{Math.round(value)}</span>
      </div>
      <div style={{ height: size }} className="w-full">
        <Viz value={value} />
      </div>
      {delta !== 0 && (
        <div
          className={`absolute top-2 right-2 text-[10px] font-black px-2 py-0.5 border border-black rounded-full ${
            delta > 0 ? "bg-brand-mint text-white" : "bg-brand-pink text-white"
          }`}
        >
          {delta > 0 ? "+" : ""}
          {delta}
        </div>
      )}
    </div>
  );
}

export function MetricsGrid({ metrics, deltas = {}, compact = false }) {
  const keys = ["mood", "aura", "trust", "safety", "stress"];
  return (
    <div
      className={`grid gap-3 ${
        compact ? "grid-cols-5" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-5"
      }`}
    >
      {keys.map((k) => (
        <MetricTile key={k} metric={k} value={metrics[k] ?? 0} delta={deltas[k] || 0} size={compact ? 70 : 100} />
      ))}
    </div>
  );
}
